<template>
    <section v-if="campaigns.length" class="flex flex-col gap-2">
        <div class="scaffold-details-small text-ink-muted">{{ t("reflectionsAdmin.previousCycles.heading") }}</div>
        <!-- Mounted once and left in place so each new result count is announced. -->
        <p class="sr-only" role="status">{{ resultAnnouncement }}</p>

        <BaseCard padding="md" class="flex flex-col gap-3">
            <div class="flex items-center gap-2 flex-wrap">
                <BaseSelect
                    :model-value="templateId"
                    :options="templateOptions"
                    :aria-label="t('reflectionsAdmin.previousCycles.template.label')"
                    trigger-class="min-w-44"
                    @update:model-value="selectTemplate" />
                <BaseSelect
                    :model-value="range"
                    :options="rangeOptions"
                    :aria-label="t('reflectionsAdmin.previousCycles.dateRange.label')"
                    trigger-class="min-w-44"
                    @update:model-value="selectRange" />
                <template v-if="range === CUSTOM_RANGE">
                    <label :for="startInputId" class="flex items-center gap-2 details text-ink-muted">
                        {{ t("reflectionsAdmin.previousCycles.dateRange.from") }}
                        <input :id="startInputId" v-model="customStart" type="date" :max="customEnd || today" class="date-input" />
                    </label>
                    <label :for="endInputId" class="flex items-center gap-2 details text-ink-muted">
                        {{ t("reflectionsAdmin.previousCycles.dateRange.to") }}
                        <input :id="endInputId" v-model="customEnd" type="date" :min="customStart || undefined" :max="today" class="date-input" />
                    </label>
                </template>
                <span class="ml-auto details text-ink-muted" aria-hidden="true">{{ resultAnnouncement }}</span>
            </div>
            <p v-if="customRangeError" class="details text-status-error" role="alert">{{ customRangeError }}</p>
        </BaseCard>

        <p v-if="!customRangeError && filteredCampaigns.length === 0" class="body-small text-ink-muted px-1">{{ t("reflectionsAdmin.previousCycles.noMatches") }}</p>
        <AdminCampaignRow
            v-for="campaign in filteredCampaigns"
            :key="campaign.id"
            :campaign="campaign"
            :busy="false"
            :expanded="expandedCampaignId === campaign.id"
            :hide-rater-status="hideRaterStatus"
            @view="emit('view', $event)"
            @toggle="onToggleCampaign" />
    </section>
</template>

<script setup lang="ts">
import type { AdminCampaignSummary } from "~vue/api/generated/reflections-admin";
import BaseCard from "~vue/components/BaseCard.vue";
import BaseSelect from "~vue/components/BaseSelect.vue";
import AdminCampaignRow from "~vue/pages/reflections/AdminCampaignRow.vue";
import {
    ALL_TEMPLATES,
    campaignClosedInRange,
    closedCampaignTemplates,
    CUSTOM_RANGE,
    presetRangeStart,
    type PreviousCyclesRangeKey,
    sortClosedCampaignsNewestFirst,
} from "~vue/pages/reflections/previousCycles";
import { DateTime } from "luxon";
import type { AcceptableValue } from "reka-ui";
import { computed, ref, useId, watch } from "vue";
import { useI18n } from "vue-i18n";

interface Props {
    campaigns: AdminCampaignSummary[];
    hideRaterStatus?: boolean;
}

const { campaigns, hideRaterStatus = false } = defineProps<Props>();

const startInputId = useId();
const endInputId = useId();
// Shared with the active list so only one campaign is open across the whole hub.
const expandedCampaignId = defineModel<string | null>("expandedCampaignId", { required: true });
const emit = defineEmits<{
    (event: "view", campaignId: string): void;
}>();
const { t } = useI18n();

const templateId = ref<string>(ALL_TEMPLATES);
const range = ref<PreviousCyclesRangeKey>("all");
const customStart = ref<string | null>(null);
const customEnd = ref<string | null>(null);

const today = DateTime.local().toISODate();

const templateOptions = computed<{ key: string; label: string }[]>(() => [
    { key: ALL_TEMPLATES, label: t("reflectionsAdmin.previousCycles.template.all") },
    ...closedCampaignTemplates(campaigns),
]);

// Literal keys per option: the i18n catalogue check can't follow a dynamic key.
const rangeOptions = computed<{ key: string; label: string }[]>(() => [
    { key: "all", label: t("reflectionsAdmin.previousCycles.dateRange.all") },
    { key: "last_30_days", label: t("reflectionsAdmin.previousCycles.dateRange.last_30_days") },
    { key: "last_90_days", label: t("reflectionsAdmin.previousCycles.dateRange.last_90_days") },
    { key: "last_6_months", label: t("reflectionsAdmin.previousCycles.dateRange.last_6_months") },
    { key: "last_12_months", label: t("reflectionsAdmin.previousCycles.dateRange.last_12_months") },
    { key: CUSTOM_RANGE, label: t("reflectionsAdmin.previousCycles.dateRange.custom") },
]);

const customRangeError = computed(() =>
    customStart.value && customEnd.value && customEnd.value < customStart.value ? t("reflectionsAdmin.previousCycles.dateRange.error") : null,
);

// Both halves of a custom range apply independently; a contradictory pair matches
// nothing, which is what the error message above the list is already saying.
const dateRange = computed(() =>
    range.value === CUSTOM_RANGE ? { startDate: customStart.value, endDate: customEnd.value } : { startDate: presetRangeStart(range.value), endDate: null },
);

const filteredCampaigns = computed(() =>
    sortClosedCampaignsNewestFirst(
        campaigns.filter((campaign) => (templateId.value === ALL_TEMPLATES || campaign.template.id === templateId.value) && campaignClosedInRange(campaign, dateRange.value)),
    ),
);

const resultAnnouncement = computed(() =>
    filteredCampaigns.value.length
        ? t("reflectionsAdmin.previousCycles.showing", { shown: filteredCampaigns.value.length, total: campaigns.length })
        : t("reflectionsAdmin.previousCycles.noMatches"),
);

function selectTemplate(next: AcceptableValue) {
    templateId.value = String(next);
}

function selectRange(next: AcceptableValue) {
    range.value = String(next) as PreviousCyclesRangeKey;
}

function onToggleCampaign(campaignId: string) {
    expandedCampaignId.value = expandedCampaignId.value === campaignId ? null : campaignId;
}

watch(range, (next) => {
    if (next === CUSTOM_RANGE && !customEnd.value) {
        customEnd.value = today;
    }
});

// A filter that hides the open row collapses it: leaving it expanded would point
// the shared model at a card that is not on screen.
watch(filteredCampaigns, (list) => {
    if (expandedCampaignId.value && !list.some((campaign) => campaign.id === expandedCampaignId.value)) {
        expandedCampaignId.value = null;
    }
});
</script>

<style scoped>
@reference "../../../../css/tailwind.css";

@layer components {
    .date-input {
        @apply rounded-lg p-2 border border-border-strong details text-ink-muted bg-surface-raised;
    }
}
</style>
